import { useEffect, useMemo, useState } from 'react';

import type { SowSummary } from '../../../services/api';
import { useCreditMutations, useSowWriteAccess } from '../../../hooks/useApi';
import { getMutationErrorMessage } from '../../../lib/apiErrors';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { formCheckboxListSurfaceClassName } from '../../ui/formPrimitives';
import { Input } from '../../ui/input';
import { Actions, DialogFrame, Field, FormErrorAlert } from './creditDialogPrimitives';
import { buildSowUserSearchOptions, filterSowUserSearchOptions } from './sowUserSearch';

function writeAccessStateClass(enabled: boolean): string {
    return enabled ? 'text-amber-800' : 'text-neutral-500';
}

export function SowWriteAccessSection({ sow }: { sow: SowSummary }) {
    const [isEditOpen, setIsEditOpen] = useState(false);
    const enabled = sow.writeProtectionEnabled ?? false;

    return (
        <div>
            <p className="text-sm text-neutral-500">Write access</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
                <span
                    className={cn('text-sm font-semibold', writeAccessStateClass(enabled))}
                    aria-live="polite"
                >
                    {enabled ? 'On' : 'Off'}
                </span>
                <Button variant="outline" size="sm" onClick={() => setIsEditOpen(true)}>
                    Manage
                </Button>
            </div>

            {isEditOpen ? (
                <SowWriteAccessDialog open onClose={() => setIsEditOpen(false)} sow={sow} />
            ) : null}
        </div>
    );
}

function SowWriteAccessDialog({
    open,
    onClose,
    sow,
}: {
    open: boolean;
    onClose: () => void;
    sow: SowSummary;
}) {
    const writeAccessQuery = useSowWriteAccess(sow.id, { enabled: open });
    const { updateSowWriteAccess } = useCreditMutations();

    const [enabled, setEnabled] = useState(false);
    const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [hydratedFromServer, setHydratedFromServer] = useState(false);

    useEffect(() => {
        if (!open) {
            setHydratedFromServer(false);
            setSearchQuery('');
            return;
        }
        if (!writeAccessQuery.data?.writeAccess || hydratedFromServer) {
            return;
        }
        const writeAccess = writeAccessQuery.data.writeAccess;
        setEnabled(writeAccess.enabled);
        setSelectedUserIds(writeAccess.users.map((user) => user.userId));
        setSearchQuery('');
        setError(null);
        setHydratedFromServer(true);
    }, [open, writeAccessQuery.data, hydratedFromServer]);

    const candidates = useMemo(() => {
        if (!enabled) {
            return [];
        }
        const allowlistedUsers = writeAccessQuery.data?.writeAccess.users ?? [];
        const eligibleOptions = buildSowUserSearchOptions(
            writeAccessQuery.data?.writeAccess.eligibleUsers ?? [],
        );
        const eligibleUserIds = new Set(eligibleOptions.map((option) => option.userId));
        const byId = new Map(
            eligibleOptions.map((option) => [option.userId, { ...option, isEligible: true }]),
        );
        // Keep already-allowlisted users visible even if they are no longer team members.
        for (const option of buildSowUserSearchOptions(allowlistedUsers)) {
            if (!eligibleUserIds.has(option.userId)) {
                byId.set(option.userId, { ...option, isEligible: false });
            }
        }
        return filterSowUserSearchOptions(
            [...byId.values()].sort((a, b) => a.label.localeCompare(b.label)),
            searchQuery,
        );
    }, [enabled, searchQuery, writeAccessQuery.data?.writeAccess]);

    const toggleUser = (userId: number) => {
        setSelectedUserIds((current) =>
            current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId],
        );
    };

    const handleSubmit = async () => {
        if (!hydratedFromServer || writeAccessQuery.isError) {
            return;
        }
        setError(null);
        try {
            await updateSowWriteAccess.mutateAsync({
                sowId: sow.id,
                request: { enabled, userIds: selectedUserIds },
            });
            onClose();
        } catch (err) {
            setError(getMutationErrorMessage(err, 'Could not update write access'));
        }
    };

    const isFormLoading = !hydratedFromServer && !writeAccessQuery.isError;
    const canSave = hydratedFromServer && !writeAccessQuery.isError;
    const emptyMembersMessage =
        (sow.SowTeams?.length ?? 0) === 0
            ? "No members found across this company's active teams. Add members to a team first."
            : "No members found on this SOW's linked teams. Add members to a linked team first.";

    return (
        <DialogFrame
            open={open}
            title="Write access"
            description="Team members still see this SOW. When protection is on, only the selected users may charge it."
            onClose={onClose}
        >
            <div className="space-y-4">
                {error ? <FormErrorAlert message={error} /> : null}

                {isFormLoading ? (
                    <p className="text-sm text-neutral-500">Loading write access…</p>
                ) : writeAccessQuery.isError ? (
                    <p className="text-sm text-red-600">
                        Could not load write access. Close and try again.
                    </p>
                ) : (
                    <>
                        <label className="flex cursor-pointer items-start gap-2 text-sm">
                            <input
                                type="checkbox"
                                className="mt-0.5 rounded border-neutral-300"
                                checked={enabled}
                                onChange={(event) => setEnabled(event.target.checked)}
                            />
                            <span>
                                <span className="font-semibold text-neutral-950">
                                    Enable write protection
                                </span>
                                <span className="block text-xs text-neutral-600">
                                    While on, only allowlisted users can make charges against this
                                    SOW. An empty allowlist blocks everyone.
                                </span>
                            </span>
                        </label>

                        {enabled ? (
                            <Field label="Users allowed to charge">
                                <Input
                                    type="search"
                                    aria-label="Search users allowed to charge"
                                    placeholder="Search by name, email, or user ID"
                                    value={searchQuery}
                                    onChange={(event) => setSearchQuery(event.target.value)}
                                />
                                <div
                                    className={cn(
                                        formCheckboxListSurfaceClassName,
                                        'mt-2 max-h-64 space-y-2 overflow-y-auto',
                                    )}
                                >
                                    {candidates.length === 0 ? (
                                        <p className="text-sm text-neutral-500">
                                            {searchQuery.trim()
                                                ? 'No users match this search.'
                                                : emptyMembersMessage}
                                        </p>
                                    ) : (
                                        candidates.map((candidate) => (
                                            <label
                                                key={candidate.userId}
                                                className="flex cursor-pointer items-center gap-2 text-sm"
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="rounded border-neutral-300"
                                                    checked={selectedUserIds.includes(
                                                        candidate.userId,
                                                    )}
                                                    onChange={() => toggleUser(candidate.userId)}
                                                />
                                                <span>{candidate.label}</span>
                                                {candidate.email ? (
                                                    <span className="text-xs text-neutral-400">
                                                        {candidate.email}
                                                    </span>
                                                ) : null}
                                                {!candidate.isEligible ? (
                                                    <span className="text-xs text-amber-700">
                                                        (no longer a team member)
                                                    </span>
                                                ) : null}
                                            </label>
                                        ))
                                    )}
                                </div>
                            </Field>
                        ) : null}
                    </>
                )}

                <Actions onCancel={onClose}>
                    <Button
                        type="button"
                        onClick={handleSubmit}
                        disabled={updateSowWriteAccess.isPending || !canSave}
                    >
                        {updateSowWriteAccess.isPending ? 'Saving…' : 'Save'}
                    </Button>
                </Actions>
            </div>
        </DialogFrame>
    );
}
